
import Vue from "vue";
import moment from "moment";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { db } from "@/utils/dexie";
import MonthControl from "./MonthControl.vue";
import { chartMixin } from "@/mixins/chartMixin";
import { loadPgFeeRates, calculatePgFeeForItem } from "@/utils/pgFee";
import eventBus from "@/eventBus";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";

import { Chart as ChartJS, registerables, ChartConfiguration } from "chart.js";
ChartJS.register(...registerables);

export default Vue.extend({
  name: "DailyPgFeeChart",
  mixins: [chartMixin],
  components: {
    MonthControl,
  },
  props: {
    calendarValue: Object as () => moment.Moment,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    chart: null as ChartJS | null,
    isRendering: false,
    hasPendingRender: false,
    pgRates: loadPgFeeRates(),
  }),
  watch: {
    calendarValue: {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      immediate: false,
    },
    "$store.state.mall_info": {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      deep: true,
    },
    orderPathFilter: {
      handler() {
        this.$nextTick(() => this.render());
      },
      deep: true,
    },
  },
  mounted() {
    eventBus.$on("pg-fee-updated", this.onPgFeeUpdated);
    this.$nextTick(() => {
      this.render();
    });
  },
  beforeDestroy() {
    eventBus.$off("pg-fee-updated", this.onPgFeeUpdated);
  },
  methods: {
    onPgFeeUpdated() {
      this.pgRates = loadPgFeeRates();
      this.$nextTick(() => {
        this.render();
      });
    },
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    createOrUpdateChart(ctx: any, config: any) {
      this.destroyChart();
      this.chart = new ChartJS(ctx, config as any) as any;
    },
    async render() {
      if (this.isRendering) {
        this.hasPendingRender = true;
        return;
      }
      this.isRendering = true;

      try {
        const canvasEl = this.$refs.chart as HTMLCanvasElement | undefined;
        if (!canvasEl) return;

        const ctx = canvasEl.getContext("2d");
        if (!ctx) return;

        const shop = this.$store.state.mall_info;
        if (!shop || !shop.mall_id || shop.shop_no === undefined) {
          return;
        }

        const start_date = this.calendarValue
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end_date = this.calendarValue
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        const dates = getDatesFromStartEnd(start_date, end_date);

        let dbItems = await db.results
          .where("[mall_id+shop_no]")
          .equals([shop.mall_id, String(shop.shop_no)])
          .toArray();

        const hasNoItems = dbItems.length === 0;
        const validShopNo = shop.shop_no !== undefined;
        if (hasNoItems && validShopNo && !isNaN(Number(shop.shop_no))) {
          const numItems = await db.results
            .where("[mall_id+shop_no]")
            .equals([shop.mall_id, Number(shop.shop_no)])
            .toArray();
          if (numItems.length > 0) {
            dbItems = numItems;
          }
        }

        const rows = {} as Record<string, any>;
        for (const item of dbItems) {
          if (item && item.date >= start_date && item.date <= end_date) {
            rows[item.date] = filterSettlementResult(
              item,
              this.orderPathFilter
            );
          }
        }

        const dsBlue =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-link")
            .trim() || "#0070f3";
        const dsAmber = "#f5a623";
        const dsHairline =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-hairline")
            .trim() || "#ebebeb";

        const labels = dates;

        const netData: (number | null)[] = [];
        const feeData: (number | null)[] = [];

        labels.forEach((date) => {
          const r = rows[date];
          if (
            !r ||
            (r.payment_composition_amount === undefined &&
              r.refund_amount === undefined &&
              r.total_amount === undefined)
          ) {
            netData.push(null);
            feeData.push(null);
            return;
          }

          let fee = 0;
          if (Array.isArray(r.orders) && r.orders.length > 0) {
            for (const order of r.orders) {
              if (!order) continue;
              fee += calculatePgFeeForItem(
                order,
                this.pgRates,
                order,
                this.orderPathFilter?.presentationMode
              ).fee;
            }
          } else {
            const paymentCompositionAmount = r.payment_composition_amount || 0;
            fee = calculatePgFeeForItem(
              {
                payment_method: "card",
                payment_method_name: "신용카드",
                actual_order_amount: {
                  payment_amount: paymentCompositionAmount,
                },
              },
              this.pgRates,
              undefined,
              this.orderPathFilter?.presentationMode
            ).fee;
          }

          const paymentCompositionAmount = r.payment_composition_amount || 0;
          const refundAmt = r.refund_amount || 0;
          const totalAmt =
            r.total_amount !== undefined
              ? r.total_amount
              : paymentCompositionAmount + refundAmt;

          const net = totalAmt - fee;

          netData.push(net);
          feeData.push(fee);
        });

        const chartLabels = labels.map((l) => moment(l).format("D"));

        const existingChart = ChartJS.getChart(canvasEl);
        if (existingChart) {
          existingChart.destroy();
        }

        const chartConfig: ChartConfiguration = {
          type: "bar",
          data: {
            labels: chartLabels,
            datasets: [
              {
                type: "bar",
                label: "예상 순정산액",
                data: netData as any,
                backgroundColor: dsBlue,
                borderColor: dsBlue,
                yAxisID: "y",
              },
              {
                type: "line",
                label: "예상 PG 수수료",
                data: feeData as any,
                borderColor: dsAmber,
                backgroundColor: dsAmber,
                borderWidth: 2,
                tension: 0.2,
                pointRadius: 3,
                yAxisID: "y1",
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "index",
              intersect: false,
            },
            onClick: (event, elements) => {
              if (elements.length > 0) {
                const index = elements[0].index;
                const selectedDate = dates[index];
                eventBus.$emit("query", selectedDate, selectedDate);
              }
            },
            scales: {
              x: {
                grid: {
                  color: dsHairline,
                },
              },
              y: {
                type: "linear",
                display: true,
                position: "left",
                grid: {
                  color: dsHairline,
                },
                ticks: {
                  callback: (value) => `${Number(value).toLocaleString()}원`,
                },
              },
              y1: {
                type: "linear",
                display: true,
                position: "right",
                grid: {
                  drawOnChartArea: false,
                },
                ticks: {
                  callback: (value) => `${Number(value).toLocaleString()}원`,
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                position: "left",
                align: "start",
                labels: {
                  boxWidth: 12,
                  boxHeight: 12,
                  font: {
                    family: "Geist, Inter, sans-serif",
                    size: 11,
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || "";
                    const val = context.raw as number | null;
                    if (val === null || val === undefined) {
                      return `${label}: 데이터 없음`;
                    }
                    return `${label}: ${val.toLocaleString()}원`;
                  },
                },
              },
            },
          },
        };

        this.createOrUpdateChart(canvasEl, chartConfig);
      } catch (e) {
        console.error("DailyPgFeeChart render error:", e);
      } finally {
        this.isRendering = false;
        if (this.hasPendingRender) {
          this.hasPendingRender = false;
          this.$nextTick(() => {
            this.render();
          });
        }
      }
    },
  },
});
